import { useCallback, useMemo, useState } from 'react'

import { useFormData } from 'brief-form'
import { FilterCompanyVendorsWebPage } from 'commons/service/bank/companyDetails'
import { useTableState } from 'ui/components/Table'

export const useComponentsEssence = (
  companyId: number,
  onFilterCompanyVendorsWebPage: (filter: FilterCompanyVendorsWebPage) => void
) => {
  const [loading, setLoading] = useState(false)

  const defaultFilter = useMemo(
    () => ({
      relationshipId: companyId,
      page: 1,
      size: 10,
      vendorsName: undefined
    }),
    [companyId]
  )

  const [tableFilter, setTableFilter] = useState<FilterCompanyVendorsWebPage>(defaultFilter)

  const tableState = useTableState({ filter: {} })

  const changePagination = useCallback(
    async (page: number, size: number) => {
      setLoading(true)

      tableState.onPageChange(page, size)

      const filter = {
        ...tableFilter,
        page,
        size
      }

      setTableFilter(filter)

      await onFilterCompanyVendorsWebPage(filter)

      setLoading(false)
    },
    [onFilterCompanyVendorsWebPage, tableFilter, tableState]
  )

  const onFilterChange = async (value: FilterCompanyVendorsWebPage) => {
    setLoading(true)

    tableState.filterForm.set({ value })
    tableState.onPageChange(defaultFilter.page, tableState.size)

    const filter = {
      ...value,
      page: defaultFilter.page,
      size: defaultFilter.size
    }

    setTableFilter(filter)

    await onFilterCompanyVendorsWebPage(filter)

    setLoading(false)
  }

  const filterForm = useFormData<FilterCompanyVendorsWebPage>({
    initialValue: {
      relationshipId: companyId,
      page: defaultFilter.page,
      size: defaultFilter.size,
      vendorsName: undefined
    }
  })

  const originalChangeHandler = filterForm.config.onChange
  filterForm.config.onChange = useCallback(
    (v, e) => {
      onFilterChange(v)
      originalChangeHandler(v, e)
    },
    [originalChangeHandler, tableState]
  )

  const clearFilter = useCallback(() => {
    onFilterChange(defaultFilter)
    filterForm.set({ reset: true })
  }, [onFilterChange, defaultFilter, filterForm])

  return {
    loading,
    tableState,
    filterForm,
    changePagination,
    clearFilter
  }
}
